/**
 * Run deterministic One-Click Filing Review via evaluate API.
 */

import { evaluateDocumentReview } from '../lib/intelligenceApi';
import {
  FILING_REVIEW_PROMPT,
  normalizeFilingReviewReport,
  type FilingReviewReport,
} from '../lib/filingReviewTypes';
import { dispatchLexCounselSend } from '../lib/lexCounselSend';
import { useLexCounselStore } from '../store/lexCounselStore';

function draftTabsPayload() {
  const tabs = useLexCounselStore.getState().workspace.draftTabs;
  if (!Array.isArray(tabs)) return [];
  return tabs.map((t) => {
    const tab = t as Record<string, unknown>;
    return {
      id: typeof tab.id === 'string' ? tab.id : undefined,
      title: typeof tab.title === 'string' ? tab.title : undefined,
      contentExcerpt:
        typeof tab.contentExcerpt === 'string'
          ? tab.contentExcerpt
          : typeof tab.content === 'string'
            ? tab.content
            : undefined,
      isActive: Boolean(tab.isActive),
    };
  });
}

export async function runFilingReviewEvaluate(): Promise<FilingReviewReport | null> {
  const store = useLexCounselStore.getState();
  const { workspace } = store;
  const workspaceId = workspace.workspaceId;
  const caseId = workspace.caseId;

  store.setFilingReviewLoading(true);
  store.setFilingReviewError(null);

  if (!workspaceId || !caseId) {
    store.setFilingReviewLoading(false);
    store.setFilingReviewError('Open a case draft to run Filing Review.');
    return null;
  }

  try {
    const draftMetadata =
      (workspace.workspaceContext?.draftMetadata as Record<string, unknown> | undefined) ??
      {};
    const raw = await evaluateDocumentReview({
      workspaceId,
      caseId,
      caseData: (workspace.caseData as Record<string, unknown>) ?? {},
      proceedings: Array.isArray(workspace.proceedings) ? workspace.proceedings : [],
      draftText: workspace.draftText ?? '',
      draftTabs: draftTabsPayload(),
      draftMetadata,
      documents: [],
    });
    const report = normalizeFilingReviewReport(raw);
    store.setFilingReviewReport(report);
    store.setFilingReviewLoading(false);
    return report;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Filing review failed';
    store.setFilingReviewLoading(false);
    store.setFilingReviewError(message);
    return null;
  }
}

/** Primary CTA: evaluate + LLM synthesis in Review mode. */
export async function runOneClickFilingReview(): Promise<void> {
  const store = useLexCounselStore.getState();
  store.setEditorCounselMode('review');
  store.openPanel();
  await runFilingReviewEvaluate();
  dispatchLexCounselSend({
    text: FILING_REVIEW_PROMPT,
    scope: 'document',
    source: 'panel',
  });
}
